import { computed, ref, toValue, watch, type MaybeRefOrGetter } from 'vue';
import { getLocalTimeZone, today, type DateValue } from '@internationalized/date';
import { toast } from 'vue-sonner';
import { useAuthStore } from '@/stores/authStore';
import {
    useIssueCommandMutation,
    useLocationZonePlayerCommandEnums,
    type CommandEnumOption,
    type IssueCommandResult,
} from '@/composables/api/useLocationZonePlayerInspectorApi';
import { bulkOptionGateReason, commandGateReason, type CommandTarget } from '@/utils/commandGating';

/** What raw mode's input starts with: every raw command is a RUNCOMM#. */
export const RAW_COMMAND_PREFIX = 'RUNCOMM#';

/** The raw input's text on entering raw mode: the prefix when empty, otherwise what was typed. */
export function prefilledRawCommand(currentText: string): string {
    return currentText.trim() === '' ? RAW_COMMAND_PREFIX : currentText;
}

/** A target player record — a search row or a live inspector record. */
type PlayerRecord = Record<string, unknown>;

/**
 * Shared command-issuing form logic for Location Zone Players — the single source of
 * truth behind both the multi-select bulk bar ({@link LzpBulkIssueBar}) and the
 * single-player inspector control ({@link LzpIssueCommandSection}). Owns the command
 * selection (catalogue or raw RUNCOMM#), per-player client gating (a preview of the
 * authoritative server gating), the schedule-for-later state, and the issue mutation +
 * result toast.
 *
 * Pass the target players as a ref or getter; a single-player caller passes a
 * one-element array. The submit endpoint is array-based (`location_zone_player_ids`),
 * so one and many players are the same code path.
 */
export function useIssueCommandForm(players: MaybeRefOrGetter<PlayerRecord[]>) {
    const enumsQuery = useLocationZonePlayerCommandEnums();
    const issueMutation = useIssueCommandMutation();
    const authStore = useAuthStore();

    const selectedCommand = ref<CommandEnumOption | null>(null);

    // Raw (free-text RUNCOMM#) path — restricted to the trusted operators. The server
    // is authoritative (it re-checks on issue); this flag only decides whether to show
    // the input. When on, we send `command` instead of `command_enum_id`.
    const canIssueRawCommand = computed(() => authStore.userData?.can_issue_raw_command === true);
    const rawMode = ref(false);
    const rawCommand = ref('');

    // Raw mode opens with the RUNCOMM# prefix already typed, so the operator types only
    // the rest. Only on entering raw mode, and only over an empty input: a watch on the
    // text itself would put the prefix back the moment someone cleared it on purpose.
    watch(
        rawMode,
        (isRaw) => {
            if (isRaw) {
                rawCommand.value = prefilledRawCommand(rawCommand.value);
            }
        },
        { immediate: true }
    );

    /** Clear the raw input after an issue, leaving the prefix in place while still in raw mode. */
    function resetRawCommand(): void {
        rawCommand.value = rawMode.value ? RAW_COMMAND_PREFIX : '';
    }

    // Schedule-for-later picker (calendar date + time of day). No date picked means
    // "issue immediately"; a picked date+time queues the command for the dispatcher.
    const scheduleDate = ref<DateValue>();
    const scheduleTime = ref('12:00');
    const minScheduleDate = today(getLocalTimeZone());

    /** 'HH:mm' → its parts, or null while the field is blank or half-typed. */
    function parseScheduleTime(value: string): { hours: number; minutes: number } | null {
        const [hours, minutes] = value.split(':').map((part) => Number.parseInt(part, 10));
        return Number.isFinite(hours) && Number.isFinite(minutes) ? { hours, minutes } : null;
    }

    // Editing the time IS a scheduling intent, even without a calendar click.
    // Without this, a time-only edit left scheduleDate undefined, the payload
    // omitted scheduled_at, and the command silently issued NOW while the chip
    // still read "now" (SAU-287). Default the date to today — or tomorrow when
    // that wall-clock has already passed today, so the pick starts valid
    // instead of instantly tripping the past-schedule guard.
    //
    // The date chosen here stays PROVISIONAL until the operator touches the
    // calendar, and every later keystroke re-derives it. That re-derivation is
    // load-bearing: a native time input fires one input event per keystroke, so
    // typing "15:30" over the 12:00 default emits 13:00 → 15:00 → 15:03 → 15:30.
    // Latching on the first of those would read the transient 13:00 as the pick,
    // and at 14:00 that is already past — the command would quietly land
    // TOMORROW at 15:30. Re-deriving keeps the last keystroke authoritative.
    //
    // Today-vs-tomorrow is decided in the OPERATOR's zone even though player time
    // is the default read, matching the scheduledInPast guard below; for a player
    // far enough behind the operator this can start the pick a day later than
    // that player's own clock requires. The operator can always move it on the
    // calendar, which pins the date and stops this from re-deriving.
    let provisionalDate: DateValue | undefined;

    watch(scheduleTime, () => {
        // A date the operator chose themselves is theirs — never move it.
        const operatorPicked =
            scheduleDate.value !== undefined
            && !(provisionalDate !== undefined && scheduleDate.value.compare(provisionalDate) === 0);
        if (operatorPicked) {
            return;
        }
        const parts = parseScheduleTime(scheduleTime.value);
        if (parts === null) {
            // Field blanked mid-edit: fall back to "now" rather than inventing a
            // midnight schedule sitting behind an empty time box.
            provisionalDate = undefined;
            scheduleDate.value = undefined;
            return;
        }
        const todayDate = today(getLocalTimeZone());
        const pickedToday = todayDate.toDate(getLocalTimeZone());
        pickedToday.setHours(parts.hours, parts.minutes, 0, 0);
        provisionalDate = pickedToday.getTime() <= Date.now() ? todayDate.add({ days: 1 }) : todayDate;
        scheduleDate.value = provisionalDate;
    });

    // Any date that didn't come from the watcher above is the operator's, so the
    // provisional marker retires. This is the only place it is dropped, which is
    // why the picker's Clear button (a direct v-model write, not clearSchedule)
    // still lands correctly.
    watch(scheduleDate, (value) => {
        if (provisionalDate === undefined) {
            return;
        }
        if (value === undefined || value.compare(provisionalDate) !== 0) {
            provisionalDate = undefined;
        }
    });

    // "Player time" (default on): the picked wall-clock is read in each player's own
    // timezone. Off → the operator's browser zone applies (and is the fallback when a
    // player has no zone).
    const usePlayerTime = ref(true);
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const browserTimezoneAbbreviation = ((): string => {
        try {
            const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(new Date());
            return parts.find((part) => part.type === 'timeZoneName')?.value ?? browserTimezone;
        } catch {
            return browserTimezone;
        }
    })();

    function targetFor(record: PlayerRecord): CommandTarget {
        const version = typeof record.player_data_version === 'string' ? record.player_data_version : null;
        const playerType = record.player_type as { id?: number } | null | undefined;
        return { version, playerTypeId: playerType?.id ?? null };
    }

    /** Per-player eligibility for the chosen command (client preview of server gating). */
    const eligibility = computed(() => {
        const command = selectedCommand.value;
        if (command === null) {
            return { eligible: [] as PlayerRecord[], ineligible: [] as Array<{ row: PlayerRecord; reason: string }> };
        }
        const eligible: PlayerRecord[] = [];
        const ineligible: Array<{ row: PlayerRecord; reason: string }> = [];
        for (const row of toValue(players)) {
            const reason = commandGateReason(command, targetFor(row));
            if (reason === null) {
                eligible.push(row);
            } else {
                ineligible.push({ row, reason });
            }
        }
        return { eligible, ineligible };
    });

    function selectCommand(command: CommandEnumOption): void {
        selectedCommand.value = command;
    }

    /**
     * Why `command` should render disabled in the picker for the current
     * targets, or null to leave it enabled. Bulk semantics: disabled only when
     * EVERY target fails the client gate ({@link bulkOptionGateReason}); a
     * single-player caller's one-element array runs the same path.
     */
    function optionGateReason(command: CommandEnumOption): string | null {
        return bulkOptionGateReason(command, toValue(players).map(targetFor));
    }

    /** The command to issue: a catalogue enum id, or a raw free-text string in raw mode. */
    const trimmedRawCommand = computed(() => rawCommand.value.trim());
    const issuePayloadCommand = computed<{ command_enum_id: number } | { command: string } | null>(() => {
        if (rawMode.value) {
            // The prefix on its own is the prefill, not a command.
            return trimmedRawCommand.value === '' || trimmedRawCommand.value === RAW_COMMAND_PREFIX
                ? null
                : { command: trimmedRawCommand.value };
        }
        return selectedCommand.value ? { command_enum_id: selectedCommand.value.id } : null;
    });

    /** A human label for the toast — the catalogue name, or the raw command text. */
    const commandLabel = computed(() => (rawMode.value ? trimmedRawCommand.value : (selectedCommand.value?.display_string ?? '')));

    /** Summarize the per-player breakdown into a single toast (issued / scheduled / skipped). */
    function announceResult(label: string, data: IssueCommandResult): void {
        const segments: string[] = [];
        if (data.issued.length) {
            segments.push(`${data.issued.length} issued`);
        }
        if (data.queued.length) {
            segments.push(`${data.queued.length} scheduled`);
        }
        if (data.skipped.length) {
            const reasons = [...new Set(data.skipped.map((entry) => entry.reason))].join('; ');
            segments.push(`${data.skipped.length} skipped (${reasons})`);
        }
        const description = segments.join(' · ');
        if (data.issued.length === 0 && data.queued.length === 0) {
            toast.warning(label, { description: description || 'Nothing to do.' });
        } else {
            toast.success(label, { description });
        }
    }

    const pad = (value: number): string => String(value).padStart(2, '0');

    /** The picked date + time as a naive wall-clock 'YYYY-MM-DDTHH:mm:ss', or null. */
    const scheduledLocal = computed<string | null>(() => {
        const date = scheduleDate.value;
        if (!date) {
            return null;
        }
        const { hours, minutes } = parseScheduleTime(scheduleTime.value) ?? { hours: 0, minutes: 0 };
        return `${date.year}-${pad(date.month)}-${pad(date.day)}T${pad(hours)}:${pad(minutes)}:00`;
    });

    /** The pick as a concrete Date in the operator's zone — for the "is past" check and label. */
    const scheduledAt = computed<Date | null>(() => {
        if (!scheduleDate.value) {
            return null;
        }
        const { hours, minutes } = parseScheduleTime(scheduleTime.value) ?? { hours: 0, minutes: 0 };
        const date = scheduleDate.value.toDate(getLocalTimeZone());
        date.setHours(hours, minutes, 0, 0);
        return date;
    });

    /** The backend requires a future time; flag a past pick (in the operator's zone) so callers can block + hint. */
    const scheduledInPast = computed(() => scheduledAt.value !== null && scheduledAt.value.getTime() <= Date.now());

    /** The chip text: "now" when immediate, else the picked time tagged with how it's read. */
    const scheduleLabel = computed(() => {
        if (!scheduledAt.value) {
            return 'now';
        }
        const when = scheduledAt.value.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
        return usePlayerTime.value ? `${when} player time` : `${when} ${browserTimezoneAbbreviation}`;
    });

    function clearSchedule(): void {
        scheduleDate.value = undefined;
    }

    /**
     * Issue the chosen command to every target player. `onSuccess` runs after the result
     * toast (e.g. so a single-player control can reset its selection); the bulk bar omits it.
     */
    function issue(onSuccess?: () => void): void {
        const payloadCommand = issuePayloadCommand.value;
        const targets = toValue(players);
        // scheduledInPast guard lives here (not just on the Send button) so the raw-input
        // Enter key can't bypass it and submit a past-dated schedule the server rejects.
        if (payloadCommand === null || targets.length === 0 || scheduledInPast.value) {
            return;
        }
        const label = commandLabel.value;
        issueMutation.mutate(
            {
                ...payloadCommand,
                location_zone_player_ids: targets.map((row) => Number(row.id)),
                // A wall-clock pick goes to the server naive; it resolves each player's
                // instant in the player's own zone (player time) or the operator's.
                ...(scheduledLocal.value
                    ? {
                          scheduled_at: scheduledLocal.value,
                          use_player_timezone: usePlayerTime.value,
                          timezone: browserTimezone,
                      }
                    : {}),
            },
            {
                onSuccess: (data) => {
                    announceResult(label, data);
                    onSuccess?.();
                },
                onError: () => toast.error(`Couldn't issue ${label}`),
            }
        );
    }

    return {
        enumsQuery,
        issueMutation,
        selectedCommand,
        canIssueRawCommand,
        rawMode,
        rawCommand,
        resetRawCommand,
        scheduleDate,
        scheduleTime,
        minScheduleDate,
        usePlayerTime,
        browserTimezone,
        browserTimezoneAbbreviation,
        eligibility,
        selectCommand,
        optionGateReason,
        issuePayloadCommand,
        commandLabel,
        scheduledAt,
        scheduledInPast,
        scheduleLabel,
        clearSchedule,
        issue,
    };
}
